import type { ComponentType, SVGProps } from "react";
import { AlertIcon } from "@/app/_components/icons/property-details/alert";
import { BalconyIcon } from "@/app/_components/icons/property-details/balcony";
import { BulbIcon } from "@/app/_components/icons/property-details/bulb";
import { CarIcon } from "@/app/_components/icons/property-details/car";
import { CctvIcon } from "@/app/_components/icons/property-details/cctv";
import { ElevatorIcon } from "@/app/_components/icons/property-details/elevator";
import { GarageIcon } from "@/app/_components/icons/property-details/garage";
import { HomeIcon } from "@/app/_components/icons/property-details/home";
import { LockIcon } from "@/app/_components/icons/property-details/lock";
import { ShowerIcon } from "@/app/_components/icons/property-details/shower";
import { SwimmingIcon } from "@/app/_components/icons/property-details/swimming";
import { VideocamIcon } from "@/app/_components/icons/property-details/videocam";
import { WheelchairIcon } from "@/app/_components/icons/property-details/wheelchair";
import { WifiIcon } from "@/app/_components/icons/property-details/wifi";
import type { AmenityApiResponse } from "@/app/_libs/data/mock-amenities-api";
import type { PropertyAmenity } from "@unreserved/types/property-details/ui-types";

/**
 * Amenities Registry
 * 
 * so we basicallyy map backend amenity icons to actual icon components
 * 
 * This registry defines:
 * - Which icon component to use for each backend icon identifier
 * - Type-safe mapping between string identifiers and React components
 * 
 * Benefits:
 * - Single source of truth for icon mappings
 * - Easy to add/remove icons
 * - Type-safe icon resolution
 * - Backend can send any icon identifier
 * - Graceful fallback for unknown icons
 */

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export const AMENITY_ICON_REGISTRY: Record<string, IconComponent> = {
  alert: AlertIcon as IconComponent,
  balcony: BalconyIcon as IconComponent,
  bulb: BulbIcon as IconComponent,
  car: CarIcon as IconComponent,
  cctv: CctvIcon as IconComponent,
  elevator: ElevatorIcon as IconComponent,
  garage: GarageIcon as IconComponent,
  home: HomeIcon as IconComponent,
  lock: LockIcon as IconComponent,
  shower: ShowerIcon as IconComponent,
  swimming: SwimmingIcon as IconComponent,
  videocam: VideocamIcon as IconComponent,
  wheelchair: WheelchairIcon as IconComponent,
  wifi: WifiIcon as IconComponent,
};

export function getAmenityIcon(iconIdentifier: string): IconComponent {
  return AMENITY_ICON_REGISTRY[iconIdentifier] || AlertIcon;
}

/**
 * Build amenities array from API response
 * 
 * This function:
 * 1. Takes backend amenities data
 * 2. Maps icon identifiers to actual components
 * 3. Returns UI-ready amenity objects
 */
export function buildAmenitiesFromApi(
  amenitiesApi: AmenityApiResponse[]
): PropertyAmenity[] {
  return amenitiesApi.map((amenity, index) => ({
    id: `amenity-${index + 1}`,
    icon: amenity.icon,
    label: amenity.name,
    available: true,
  }));
}